import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import NavigationDrawer from '../Navigation/NavigationDrawer';
import MarkdownDocViewer from './MarkdownDocViewer';
import { adminDocsService } from '../../services/adminDocsService';

const DEFAULT_SLUG = 'admin-guide';

export default function AdminDocumentation() {
  const { slug } = useParams();
  const activeSlug = slug || DEFAULT_SLUG;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setDoc(null);
    try {
      const listRes = await adminDocsService.list();
      const available = listRes.docs || [];
      setDocs(available);

      const slugToLoad = available.some((d) => d.slug === activeSlug)
        ? activeSlug
        : available[0]?.slug;

      if (!slugToLoad) {
        setError('No documentation guides are available on the server.');
        return;
      }

      if (slugToLoad !== activeSlug) {
        navigate(`/admin/documentation/${slugToLoad}`, { replace: true });
        return;
      }

      const docRes = await adminDocsService.get(slugToLoad);
      setDoc(docRes);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.message || e.message || 'Failed to load documentation');
    } finally {
      setLoading(false);
    }
  }, [activeSlug, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const selectDoc = (nextSlug) => {
    navigate(`/admin/documentation/${nextSlug}`);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection=""
        setActiveSection={() => {}}
        navigate={navigate}
      />
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflow: 'auto' }}>
        <Container maxWidth="lg" disableGutters>
          <Typography variant="h4" gutterBottom>
            Documentation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Internal admin guides for SecurePoint operations.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Paper variant="outlined" sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
              <List dense disablePadding>
                {docs.map((item) => (
                  <ListItemButton
                    key={item.slug}
                    selected={item.slug === activeSlug}
                    onClick={() => selectDoc(item.slug)}
                  >
                    <ListItemText
                      primary={item.title}
                      secondary={item.description}
                      primaryTypographyProps={{ fontWeight: item.slug === activeSlug ? 700 : 400 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>

            <Paper variant="outlined" sx={{ flex: 1, p: { xs: 2, md: 3 }, minHeight: 320 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : doc ? (
                <MarkdownDocViewer content={doc.content} />
              ) : (
                <Typography color="text.secondary">Select a guide from the list.</Typography>
              )}
            </Paper>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
